import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useMediaQuery,
  TextField,
  Box,
  Grid,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Alert,
  LinearProgress,
  Tooltip
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Save as SaveIcon,
  Create as SignIcon,
  ArrowBack as BackIcon,
  Clear as ClearIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Assignment as NoteIcon,
  MedicalServices as MedicalIcon,
  LocalHospital as HospitalIcon,
  Comment as CommentIcon
} from "@mui/icons-material";
import SignatureCanvas from "react-signature-canvas";
import PatientInfo from "../components/procedure_note/patientInfo";
import InterimHPI from "../components/procedure_note/interimHpi";
import ProcedurePerformed from "../components/procedure_note/procedurePerformed";
import TopNav from "../components/topnav";
import ICD10Codes from "../components/icd10codes";
import Authorization from "../components/procedure_note/Authorization";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { Context } from "../context";
import { secureApi, ADMIN, MEDICAL_ASSISTANT } from "../config";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  formContainer: {
    paddingTop: 20,
    paddingBottom: 40,
  },
  pageHeader: {
    marginBottom: 20,
    textAlign: "center",
  },
  formTitle: {
    marginBottom: 20,
    textAlign: "center",
    fontWeight: "500",
  },
  sectionTitle: {
    fontWeight: "bold",
    marginBottom: 12,
    marginTop: 15,
  },
  stepperContainer: {
    marginBottom: 30,
  },
  signatureBox: {
    border: "1px solid #ccc",
    borderRadius: 4,
    padding: 10,
    minHeight: 60,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  signatureImg: {
    maxHeight: "50px",
    maxWidth: "100%",
  },
  actionButtons: {
    marginTop: 30,
  },
  sectionCard: {
    marginBottom: 20,
  }
}));

const today = new Date();
const date = today.getDate();
const month = today.getMonth() + 1;
const year = today.getFullYear();

const ProcedureNote = () => {
  const classes = useStyles();
  const { patient, setPatient, setLoggedIn, user } = useContext(Context);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const patient_id = params.get("patient_id"); // patient ID from the url link parameter
  const [patientID, setPatientID] = useState(null); // patient ID from a saved procedure note
  const { procedure_note_id } = useParams();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && user.role !== ADMIN && user.role !== MEDICAL_ASSISTANT) {
      toast.error("You don't have permission to access this page");
      navigate("/");
    }
  }, [user, navigate]);

  // Patient and procedure information
  const [patientName, setPatientName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [dateOfProcedureNote, setDateOfProcedureNote] = useState(
    dayjs(`${month}/${date}/${year}`)
  );
  const [surgeon, setSurgeon] = useState("Alexander Movshis");
  const [assistant, setAssistant] = useState("none");
  const [anesthesia, setAnesthesia] = useState("local");
  const [anesthesiologist, setAnesthesiologist] = useState("none");
  const [neuroMonitoring, setNeuroMonitoring] = useState("none");
  const [typeOfFacility, setTypeOfFacility] = useState("Medical Office");
  const [nameOfFacility, setNameOfFacility] = useState("");
  const [officeLocation, setOfficeLocation] = useState("");

  // Note content
  const [note, setNote] = useState(null);
  const [interimHPI, setInterimHPI] = useState("");
  const [performedProcedure, setPerformedProcedure] = useState({});
  const [comments, setComments] = useState("");
  const [icd10codes, setIcd10codes] = useState([]);

  // Authorization information
  const [hasAuthorization, setHasAuthorization] = useState(false);
  const [authorization, setAuthorization] = useState("");
  const [authorizationValidityDates, setAuthorizationValidityDates] = useState("");

  // Signature handling
  const [signPad, setSignPad] = useState({});
  const [signModalOpen, setSignModalOpen] = useState(false);
  const [signed, setSigned] = useState(false);
  
  // Confirmation dialogs
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  // Steps for the stepper
  const steps = [
    'Patient Information',
    'Interim HPI',
    'Procedure Details',
    'Diagnosis & Authorization',
    'Finalize Note'
  ];

  const getStepIcon = (index) => {
    const icons = [
      <HospitalIcon />,
      <NoteIcon />,
      <MedicalIcon />,
      <CommentIcon />,
      <SignIcon />
    ];
    return icons[index];
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    window.scrollTo(0, 0);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    if (patient_id) {
      setLoading(true);
      secureApi(window.localStorage.getItem("alex-med-token"))
        .get(`/patient/${patient_id}`)
        .then((response) => {
          const { data: patient } = response;
          setPatient(patient);
          if (!patient.dateOfService || patient.dateOfService.length === 0) {
            toast.warn("Create initial consult note first!");
            navigate(`/patient/${patient_id}`);
          }
          setLoading(false);
        })
        .catch((e) => {
          console.log(e);
          if (e.response && e.response.status === 401) {
            toast.warn("Session has been expired. You need to login again!");
            setLoggedIn(false);
            navigate("/login");
          } else if (e.response) {
            toast.error(e.response.data.message);
          } else {
            toast.error("An error occurred while loading patient data.");
          }
          setLoading(false);
        });
    } else if (procedure_note_id) {
      setLoading(true);
      secureApi(window.localStorage.getItem("alex-med-token"))
        .get(`/procedure-note/${procedure_note_id}`)
        .then((response) => {
          const { data: procedureNote } = response;
          setNote(procedureNote);
          setDateOfProcedureNote(dayjs(procedureNote.dateOfProcedure));
          setSurgeon(procedureNote.surgeon);
          setAssistant(procedureNote.assistant);
          setAnesthesia(procedureNote.anesthesia);
          setAnesthesiologist(procedureNote.anesthesiologist);
          setNeuroMonitoring(procedureNote.neuroMonitoring);
          setTypeOfFacility(procedureNote.typeOfFacility);
          setNameOfFacility(procedureNote.nameOfFacility);
          if (procedureNote.officeLocation)
            setOfficeLocation(procedureNote.officeLocation);
          setInterimHPI(procedureNote.interimHPI);
          setPerformedProcedure(procedureNote.performedProcedure);
          setComments(procedureNote.comments);
          setPatientID(procedureNote.patient_id);
          if (procedureNote.icd10codes) setIcd10codes(procedureNote.icd10codes);
          if (procedureNote.hasAuthorization)
            setHasAuthorization(procedureNote.hasAuthorization);

          secureApi(window.localStorage.getItem("alex-med-token"))
            .get(`/patient/${procedureNote.patient_id}`)
            .then((response) => {
              const { data: patient } = response;
              setPatient(patient);
              setLoading(false);
            })
            .catch((e) => {
              console.log(e);
              if (e.response && e.response.status === 401) {
                toast.warn(
                  "Session has been expired. You need to login again!"
                );
                setLoggedIn(false);
                navigate("/login");
              } else if (e.response) {
                toast.error(e.response.data.message);
              } else {
                toast.error("An error occurred while loading patient data.");
              }
              setLoading(false);
            });
        })
        .catch((e) => {
          console.log(e);
          if (e.response && e.response.status === 401) {
            toast.warn("Session has been expired. You need to login again!");
            setLoggedIn(false);
            navigate("/login");
          } else if (e.response) {
            toast.error(e.response.data.message);
          } else {
            toast.error("An error occurred while loading procedure note data.");
          }
          setLoading(false);
        });
    } else {
      toast.error("Something went wrong!");
      setLoading(false);
    }
  }, [patient_id, procedure_note_id, navigate, setLoggedIn, setPatient]);

  useEffect(() => {
    if (patient) {
      const { patientInfo, insuranceInformation } = patient;
      if (patientInfo) {
        let name = `${patientInfo.first_name} ${patientInfo.last_name}`;
        setPatientName(name);
        setDateOfBirth(patientInfo.date_of_birth);
      }

      if (insuranceInformation) {
        const { botox_authorization, botox_authorization_validity_dates } =
          insuranceInformation;
        if (botox_authorization) {
          setAuthorization(botox_authorization);
        }
        if (botox_authorization_validity_dates) {
          const start = new Date(botox_authorization_validity_dates["start"]);
          const end = new Date(botox_authorization_validity_dates["end"]);
          setAuthorizationValidityDates(
            `${start.toLocaleDateString("en-US")} - ${end.toLocaleDateString(
              "en-US"
            )}`
          );
        }
      }
    }
  }, [patient]);

  const handleCancelModalOpen = () => {
    setCancelModalOpen(true);
  };
  
  const handleCancelModalClose = () => {
    setCancelModalOpen(false);
  };
  
  const handleCancel = () => {
    window.scrollTo(0, 0);
    navigate(`/patient/${patient_id || patientID}`);
  };
  
  const handleClear = () => {
    signPad.clear();
    setSigned(false);
  };
  
  const handleSignModalClose = (event, reason) => {
    if (reason !== "backdropClick") {
      handleClear();
      setSignModalOpen(false);
    }
  };
  
  const handleSave = (withSignature = false) => {
    let physicalSign = null;
    let status = "unsigned";
    
    if (withSignature && signed) {
      physicalSign = signPad.getTrimmedCanvas().toDataURL("image/png");
      status = "signed";
      handleSignModalClose();
    }
    
    setLoading(true);
    
    const noteData = {
      patient_id: patient_id || patientID,
      patientName,
      dateOfBirth,
      dateOfProcedure: dateOfProcedureNote.format("MM/DD/YYYY"),
      surgeon,
      assistant,
      anesthesia,
      anesthesiologist,
      neuroMonitoring,
      typeOfFacility,
      nameOfFacility,
      officeLocation,
      interimHPI,
      performedProcedure,
      comments,
      icd10codes,
      status,
      hasAuthorization,
      authorization,
      authorizationValidityDates,
      physicalSign,
    };
    
    if (patient_id) {
      secureApi(window.localStorage.getItem("alex-med-token"))
        .post(`/procedure-note`, noteData)
        .then((response) => {
          const { noteId } = response.data;
          toast.success("New procedure note has been created successfully!");
          navigate(`/procedure-note/${noteId}`);
        })
        .catch((e) => {
          console.log(e);
          if (e.response && e.response.status === 401) {
            toast.warn("Session has been expired. You need to login again!");
            setLoggedIn(false);
            navigate("/login");
          } else if (e.response) {
            toast.error(e.response.data.message);
          } else {
            toast.error("An error occurred while saving the procedure note.");
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (procedure_note_id) {
      secureApi(window.localStorage.getItem("alex-med-token"))
        .put(`/procedure-note/${procedure_note_id}`, noteData)
        .then(() => {
          toast.success("Procedure note has been updated successfully!");
        })
        .catch((e) => {
          console.log(e);
          if (e.response && e.response.status === 401) {
            toast.warn("Session has been expired. You need to login again!");
            setLoggedIn(false);
            navigate("/login");
          } else if (e.response) {
            toast.error(e.response.data.message);
          } else {
            toast.error("An error occurred while updating the procedure note.");
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      toast.error("Something went wrong!");
      setLoading(false);
    }
  };
  
  const handleSign = () => {
    handleSave(true);
  };

  const renderPatientInfo = () => (
    <>
      <Typography variant="h5" className={classes.formTitle}>
        Patient Information
      </Typography>
      
      <Card elevation={2} className={classes.sectionCard}>
        <CardContent>
          <PatientInfo
            patientName={patientName}
            dateOfBirth={dateOfBirth}
            dateOfProcedureNote={dateOfProcedureNote}
            setDateOfProcedureNote={setDateOfProcedureNote}
            surgeon={surgeon}
            setSurgeon={setSurgeon}
            assistant={assistant}
            setAssistant={setAssistant}
            anesthesia={anesthesia}
            setAnesthesia={setAnesthesia}
            anesthesiologist={anesthesiologist}
            setAnesthesiologist={setAnesthesiologist}
            neuroMonitoring={neuroMonitoring}
            setNeuroMonitoring={setNeuroMonitoring}
            typeOfFacility={typeOfFacility}
            setTypeOfFacility={setTypeOfFacility}
            nameOfFacility={nameOfFacility}
            setNameOfFacility={setNameOfFacility}
            officeLocation={officeLocation}
            setOfficeLocation={setOfficeLocation}
          />
        </CardContent>
      </Card>
      
      <Box display="flex" justifyContent="flex-end">
        <Button 
          variant="contained" 
          onClick={handleNext}
          endIcon={<CheckIcon />}
        >
          Continue
        </Button>
      </Box>
    </>
  );

  const renderInterimHPI = () => (
    <>
      <Typography variant="h5" className={classes.formTitle}>
        Interim History of Present Illness
      </Typography>
      
      <Card elevation={2} className={classes.sectionCard}>
        <CardContent>
          <InterimHPI interimHPI={interimHPI} setInterimHPI={setInterimHPI} />
        </CardContent>
      </Card>
      
      <Box display="flex" justifyContent="space-between">
        <Button 
          variant="outlined" 
          onClick={handleBack}
          startIcon={<BackIcon />}
        >
          Back
        </Button>
        
        <Button 
          variant="contained" 
          onClick={handleNext}
          endIcon={<CheckIcon />}
        >
          Continue
        </Button>
      </Box>
    </>
  );

  const renderProcedureDetails = () => (
    <>
      <Typography variant="h5" className={classes.formTitle}>
        Procedure Details
      </Typography>
      
      <Card elevation={2} className={classes.sectionCard}>
        <CardContent>
          <ProcedurePerformed
            custom={patient_id ? false : true}
            performedProcedure={performedProcedure}
            setPerformedProcedure={setPerformedProcedure}
          />
        </CardContent>
      </Card>
      
      <Box display="flex" justifyContent="space-between">
        <Button 
          variant="outlined" 
          onClick={handleBack}
          startIcon={<BackIcon />}
        >
          Back
        </Button>
        
        <Button 
          variant="contained" 
          onClick={handleNext}
          endIcon={<CheckIcon />}
        >
          Continue
        </Button>
      </Box>
    </>
  );

  const renderDiagnosisAndAuth = () => (
    <>
      <Typography variant="h5" className={classes.formTitle}>
        Diagnosis & Authorization
      </Typography>
      
      <Card elevation={2} className={classes.sectionCard}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            ICD-10 Diagnosis Codes
          </Typography>
          <ICD10Codes icd10codes={icd10codes} setIcd10codes={setIcd10codes} />
        </CardContent>
      </Card>
      
      <Card elevation={2} className={classes.sectionCard}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            Authorization Information
          </Typography>
          <Authorization
            hasAuthorization={hasAuthorization}
            setHasAuthorization={setHasAuthorization}
            authorization={authorization}
            authorizationValidityDates={authorizationValidityDates}
          />
        </CardContent>
      </Card>
      
      <Box display="flex" justifyContent="space-between">
        <Button 
          variant="outlined" 
          onClick={handleBack}
          startIcon={<BackIcon />}
        >
          Back
        </Button>
        
        <Button 
          variant="contained" 
          onClick={handleNext}
          endIcon={<CheckIcon />}
        >
          Continue
        </Button>
      </Box>
    </>
  );

  const renderFinalize = () => (
    <>
      <Typography variant="h5" className={classes.formTitle}>
        Finalize Procedure Note
      </Typography>
      
      <Card elevation={2} className={classes.sectionCard}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            Comments
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Additional Comments"
            placeholder="Enter any additional comments about the procedure"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            variant="outlined"
          />
        </CardContent>
      </Card>
      
      <Card elevation={2} className={classes.sectionCard}>
        <CardContent>
          <Typography variant="h6" className={classes.sectionTitle}>
            Sign and Complete
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Please review all information before finalizing this procedure note. Once finalized, 
              this document becomes part of the patient's medical record.
            </Typography>
          </Alert>
          
          {note && note.status === "signed" && (
            <Box display="flex" alignItems="center" mb={2}>
              <Typography variant="subtitle1" sx={{ mr: 2 }}>
                Signature:
              </Typography>
              {note.physicalSign && (
                <Box className={classes.signatureBox} sx={{ width: "300px" }}>
                  <img
                    src={note.physicalSign}
                    alt="Signature"
                    className={classes.signatureImg}
                  />
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
      
      <Box display="flex" justifyContent="space-between" className={classes.actionButtons}>
        <Button 
          variant="outlined" 
          onClick={handleBack}
          startIcon={<BackIcon />}
        >
          Back
        </Button>
        
        <Box>
          <Button
            variant="outlined"
            color="error"
            onClick={handleCancelModalOpen}
            sx={{ mr: 2 }}
          >
            Close
          </Button>
          
          {(note === null || note.status === "unsigned") && (
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => handleSave(false)}
              endIcon={<SaveIcon />}
              sx={{ mr: 2 }}
              disabled={loading}
            >
              Save
            </Button>
          )}
          
          <Button
            variant="contained"
            color="success"
            onClick={() => setSignModalOpen(true)}
            endIcon={<SignIcon />}
            disabled={loading}
          >
            Sign and Save
          </Button>
        </Box>
      </Box>
    </>
  );

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return renderPatientInfo();
      case 1:
        return renderInterimHPI();
      case 2:
        return renderProcedureDetails();
      case 3:
        return renderDiagnosisAndAuth();
      case 4:
        return renderFinalize();
      default:
        return 'Unknown step';
    }
  };

  return (
    <>
      <TopNav />
      {loading && <LinearProgress />}
      <Container maxWidth="lg" className={classes.formContainer}>
        <Paper elevation={2} sx={{ p: 4 }}>
          <Typography variant="h4" className={classes.pageHeader} color="primary">
            Modal Medical Services PLLC
          </Typography>
          
          <Typography variant="h5" align="center" sx={{ mb: 1 }}>
            Pain Management Service
          </Typography>
          
          <Typography variant="h5" align="center" fontWeight="bold" sx={{ mb: 4 }}>
            Procedure Note
          </Typography>
          
          <Box className={classes.stepperContainer}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel StepIconComponent={() => getStepIcon(index)}>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
          
          {getStepContent(activeStep)}
        </Paper>
      </Container>
      
      {/* Confirmation Dialog */}
      <Dialog
        fullScreen={fullScreen}
        open={cancelModalOpen}
        onClose={handleCancelModalClose}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <CloseIcon color="error" sx={{ mr: 1 }} />
            Close Procedure Note
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to close this procedure note? Any unsaved changes will be lost.
            If you agree, you will be redirected to the patient page.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleCancelModalClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleCancel} autoFocus variant="contained" color="error">
            Close Note
          </Button>
        </DialogActions>
      </Dialog>

      {/* Signature Dialog */}
      <Dialog 
        open={signModalOpen} 
        onClose={handleSignModalClose} 
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <SignIcon sx={{ mr: 1 }} />
            Sign Procedure Note
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please sign below to confirm this procedure note:
          </Typography>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                border: "1px solid #ccc",
                borderRadius: "5px",
                backgroundColor: "#f9f9f9",
              }}
            >
              <SignatureCanvas
                penColor="black"
                canvasProps={{ 
                  width: 600, 
                  height: 200, 
                  className: "sigCanvas" 
                }}
                ref={(ref) => {
                  setSignPad(ref);
                }}
                onEnd={() => setSigned(true)}
              />
            </Box>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button 
            onClick={handleClear} 
            variant="outlined" 
            color="warning"
            startIcon={<ClearIcon />}
          >
            Clear
          </Button>
          <Button 
            onClick={handleSign} 
            variant="contained" 
            disabled={!signed}
            endIcon={<SignIcon />}
            color="success"
          >
            Sign and Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProcedureNote;